import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  model,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DataManagementService } from '@bizzdesign/data-access-data-management';
import { type ObjectType } from '@bizzdesign/sdk-knowledgegraph/bdcore';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import {
  type AttributeCategoryDefinitionDto,
  type AttributeDefinition,
  type DialogStatus,
  type StatusDialogSettings,
} from '@bizzdesign/shared/models';
import {
  baseRoutes,
  createBDCoreObjectTypeOptions,
} from '@bizzdesign/shared/utils';
import { DropdownComponent } from '@bizzdesign/ui-dropdown';
import { PageHeaderComponent } from '@bizzdesign/ui-page-header';
import { StatusDialogComponent } from '@bizzdesign/ui-status-dialog';
import { type TreeNode } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import {
  combineLatest,
  map,
  type Observable,
  ReplaySubject,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import { filter } from 'rxjs/operators';
import { AttributeCategoryDefinitionsListComponent } from '../../components/attribute-category-definitions-list/attribute-category-definitions-list';
import { AttributesEditorComponent } from '../../components/attributes-editor/attributes-editor.component';
import { CategorySelectorComponent } from '../../components/category-selector/category-selector.component';
import { type CategorySelection } from '../../models/category-selection.model';
import { type ExistingCategories } from '../../models/existing-categories.model';

/**
 * @deprecated will be replaced in future versions
 */
@Component({
  selector: 'bd-manage-attributes',
  imports: [
    AsyncPipe,
    ButtonModule,
    SharedI18nModule,
    AttributeCategoryDefinitionsListComponent,
    FormsModule,
    ReactiveFormsModule,
    CategorySelectorComponent,
    AttributesEditorComponent,
    StatusDialogComponent,
    DropdownComponent,
    PageHeaderComponent,
  ],
  templateUrl: './manage-attributes-container.component.html',
  styleUrls: ['./manage-attributes-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeprecatedManageAttributesContainerComponent {
  private readonly router = inject(Router);
  private readonly service = inject(DataManagementService);

  readonly objectTypeOptions = createBDCoreObjectTypeOptions();

  selectedObjectType = model<TreeNode<ObjectType>>();

  selectedObjectType$: Observable<ObjectType | undefined> = toObservable(
    this.selectedObjectType,
  ).pipe(map((selection) => selection?.data));

  attrCatDefs$: Observable<{
    objectType: ObjectType;
    categories: AttributeCategoryDefinitionDto[];
  }> = this.selectedObjectType$.pipe(
    filter((objectType) => objectType !== undefined),
    switchMap((objectType) =>
      this.service.getAttributeCategoryDefinitionsForType(objectType).pipe(
        map((categories) => ({
          objectType: objectType,
          categories: categories,
        })),
      ),
    ),
    shareReplay(1),
  );

  loading$: Observable<boolean> = combineLatest([
    this.selectedObjectType$,
    this.attrCatDefs$.pipe(startWith(null)),
  ]).pipe(
    map(
      ([selectedObjectType, attrCatDefs]) =>
        selectedObjectType !== undefined &&
        attrCatDefs?.objectType !== selectedObjectType,
    ),
  );

  isCategorySelectionReady$: Observable<boolean> = combineLatest([
    this.selectedObjectType$,
    this.attrCatDefs$,
  ]).pipe(
    map(
      ([selectedObjectType, attrCatDefs]) =>
        selectedObjectType !== undefined &&
        attrCatDefs.objectType === selectedObjectType,
    ),
  );

  existingCategories$: Observable<ExistingCategories> = this.attrCatDefs$.pipe(
    map((attrCatDefs) => ({
      objectType: attrCatDefs.objectType,
      categories: attrCatDefs.categories.map((category) => {
        return {
          id: category.id,
          readOnly: category.readOnly,
          name: category.name,
          elementType: category.elementType,
          attributeDefinitions: category.attributeDefinitions,
        };
      }),
    })),
  );

  private readonly selectedCategorySubject$ =
    new ReplaySubject<CategorySelection>(1);

  selectedCategory$: Observable<CategorySelection> =
    this.selectedCategorySubject$.asObservable();

  isAttributesEditingReady$: Observable<boolean> = combineLatest([
    this.selectedObjectType$,
    this.selectedCategory$,
  ]).pipe(
    map(
      ([selectedObjectType, selectedCategory]) =>
        selectedObjectType !== undefined &&
        selectedObjectType === selectedCategory.objectType &&
        selectedCategory.categoryId !== undefined &&
        selectedCategory.isNewCategory !== undefined,
    ),
  );

  existingAttributes$: Observable<string[]> = this.selectedCategory$.pipe(
    filter(
      (selectedCategory) =>
        selectedCategory.categoryId !== undefined &&
        selectedCategory.isNewCategory !== undefined,
    ),
    withLatestFrom(this.attrCatDefs$),
    map(([selectedCategory, attrCatDefs]) => {
      return selectedCategory.isNewCategory
        ? []
        : attrCatDefs.categories
            .find((category) => category.id === selectedCategory.categoryId)!
            .attributeDefinitions.map((attrDef) => attrDef.name);
    }),
  );

  newAttributesSubject$ = new ReplaySubject<AttributeDefinition[]>(1);
  newAttributes$ = this.newAttributesSubject$.asObservable();

  isChangeReady$: Observable<boolean> = combineLatest([
    this.isAttributesEditingReady$,
    this.newAttributes$,
  ]).pipe(
    map(
      ([isAttributesEditingReady, newAttributes]) =>
        isAttributesEditingReady && newAttributes.length > 0,
    ),
  );

  confirmButtonTrigger$ = new Subject<void>();
  completionDialogSettings$ = this.confirmButtonTrigger$.pipe(
    withLatestFrom(
      this.selectedObjectType$,
      this.selectedCategory$,
      this.newAttributes$,
    ),
    switchMap(([_, selectedType, selectedCategory, newAttributes]) => {
      const categoryId = selectedCategory.categoryId!;
      const categoryName = selectedCategory.categoryName!;
      return selectedCategory.isNewCategory
        ? this.service.createAttributeCategory(
            selectedType!,
            categoryName,
            newAttributes,
          )
        : this.service.addAttributesToAttributeCategory(
            selectedType!,
            categoryId,
            categoryName,
            newAttributes,
          );
    }),
    map((result) => {
      return result.ok
        ? {
            display: true,
            status: 'success' as DialogStatus,
            header: {
              textKey: 'manageAttributes.completionDialog.success.header',
              tScope: 'featureDataManagement',
            },
            body: {
              textKey:
                'manageAttributes.completionDialog.success.messageCreate',
              tScope: 'featureDataManagement',
            },
          }
        : {
            display: true,
            status: 'failure' as DialogStatus,
            header: {
              textKey: 'manageAttributes.completionDialog.failure.header',
              tScope: 'featureDataManagement',
            },
            body: {
              textKey:
                'manageAttributes.completionDialog.failure.messageCreate',
              tScope: 'featureDataManagement',
            },
          };
    }),
    shareReplay(1),
    startWith({
      display: false,
      status: 'failure',
      header: {
        textKey: 'manageAttributes.completionDialog.failure.header',
        tScope: 'featureDataManagement',
      },
      body: {
        textKey: 'manageAttributes.completionDialog.failure.messageCreate',
        tScope: 'featureDataManagement',
      },
    }),
  ) as Observable<StatusDialogSettings>;

  onCancelClick() {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([baseRoutes.manageAttributes]);
    });
  }

  onCategorySelected(category: CategorySelection) {
    this.selectedCategorySubject$.next(category);
  }

  onNewAttributes(attributes: AttributeDefinition[]) {
    this.newAttributesSubject$.next(attributes);
  }

  onConfirmClick() {
    this.confirmButtonTrigger$.next();
  }

  onCompletionDialogClose() {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([baseRoutes.manageAttributes]);
    });
  }
}
